import React from 'react';
import { IAgentLandingAgentData, IAgentLandingMarketStats } from 'types/agent-landing-page';

import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';

import { Eyebrow } from 'components/AgentLanding/AgentLandingPrimitives';

import DigitalAdZipCodeDataDisplay from './DigitalAdZipCodeDataDisplay';
import DigitalAdZipCodeForm from './DigitalAdZipCodeForm';

type Props = { agentLandingPageToken: string; agentData: IAgentLandingAgentData };

const DigitalAdLoadingStatus = ({ zipCode }: { zipCode: string }) => {
  const [messageIndex, setMessageIndex] = React.useState(0);
  const messages = [
    'Reviewing recent listings in',
    'Comparing sale prices and inventory in',
    'Preparing your local market profile for',
  ];

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      setMessageIndex((currentIndex) => {
        if (currentIndex >= messages.length - 2) window.clearInterval(interval);
        return Math.min(currentIndex + 1, messages.length - 1);
      });
    }, 1600);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <p>
      {messages[messageIndex]}
      {' '}
      <b>{zipCode || 'your ZIP code'}</b>
    </p>
  );
};

const DigitalAd = ({ agentLandingPageToken, agentData }: Props) => {
  const [loading, setLoading] = React.useState(false);
  const [loadingZipCode, setLoadingZipCode] = React.useState('');
  const [marketData, setMarketData] = React.useState<IAgentLandingMarketStats>();
  const loadingSectionRef = React.useRef<HTMLElement | null>(null);
  const showInitialContent = !loading && !marketData;

  React.useEffect(() => {
    if (!loading || !loadingSectionRef.current) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    loadingSectionRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [loading]);

  return (
    <>
      <DigitalAdZipCodeForm
        agentLandingPageToken={agentLandingPageToken}
        agentData={agentData}
        onLoading={(isLoading, zipCode) => {
          setLoading(isLoading);
          if (isLoading) setMarketData(undefined);
          if (zipCode) setLoadingZipCode(zipCode);
        }}
        onResult={(data) => { setMarketData(data); setLoading(false); }}
        showBenefits={showInitialContent}
        showPreview={showInitialContent}
      />
      {loading && (
        <section ref={loadingSectionRef} className="al-market-loading" aria-live="polite">
          <div className="al-loading-inner">
            <div className="al-loading-spinner" aria-hidden="true">
              <i />
              <i />
              <i />
              <span><HomeOutlinedIcon /></span>
            </div>
            <Eyebrow>Building your local snapshot</Eyebrow>
            <h2>Gathering the latest market activity…</h2>
            <DigitalAdLoadingStatus zipCode={loadingZipCode} />
            <div className="al-loading-steps" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <small>This usually takes just a few seconds.</small>
          </div>
        </section>
      )}
      {!loading && marketData && <DigitalAdZipCodeDataDisplay agentLandingPageToken={agentLandingPageToken} agentData={agentData} zipCodeData={marketData} />}
    </>
  );
};

export default DigitalAd;
